import * as React from 'react';
import {
  isDefined,
  noop,
  isHoverForInteractiveType,
  saveNodeType,
  terminate,
  getValueFromOverride,
} from 'react-financial-charts';
import { HoverTextNearMouse, MouseLocationIndicator } from '@react-financial-charts/interactive/lib/components/index';
import { EachAnchoredVolumeProfile } from './Wrapper';

import { getNewXY } from '@/utils/utils';
import { ChartShapesContext } from '@/contexts/ChartShapesContext';
import type { DragOverrideBase, DrawingSharedState, HoverTextConfig, MoreProps } from '@/types/commontypes';
import { ShapeType } from '@/types/commontypes';
import { toVolumeProfileRFCProps } from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import { volumeProfileDefaults } from '@/utils/defaultShapePropertyValues';
import type { AnchoredVolumeProfileRenderNode } from '@/types/commontypes';

interface AnchoredVolumeProfileShape {
  uuid: string;
  type?: string;
  start: [number, number]; // [x, y] - anchor point (only the start; the end is always the latest candle)
  selected: boolean;
  locked: boolean;
  chartId: string;
  appearance: Record<string, unknown>;
  hidden: boolean; // New property to control visibility
  createdAt: number;
  updatedAt: number;
}

export interface AnchoredVolumeProfileProps {
  enabled: boolean;
  chartId: string;
  trends: AnchoredVolumeProfileShape[];
  sharedState?: DrawingSharedState;
  setSharedState?: (state: DrawingSharedState) => void;
  onStart?: (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => void;
  onComplete?: (e: React.MouseEvent, trends: AnchoredVolumeProfileShape[], moreProps: MoreProps) => void;
  onSelect?: (e: React.MouseEvent, trends: AnchoredVolumeProfileShape[], moreProps: MoreProps) => void;
  onDragComplete?: (e: React.MouseEvent, trends: AnchoredVolumeProfileShape[], moreProps: MoreProps) => void;
  onHover?: (hoveredId: string | null, moreProps: MoreProps) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  hoverId?: string;
  snap?: boolean;
  snapTo?: (value: number) => number;
  shouldDisableSnap?: (e: React.MouseEvent) => boolean;
  currentPositionStroke?: string;
  currentPositionStrokeWidth?: number;
  currentPositionRadius?: number;
  currentPositionstrokeOpacity?: number;
  hoverText?: Partial<HoverTextConfig>;
  isAnyShapeEnabled?: boolean;
}

interface AnchoredVolumeProfileOverride extends DragOverrideBase {
  anchorX: number;
}

interface AnchoredVolumeProfileState {
  override?: AnchoredVolumeProfileOverride | null;
  trends?: AnchoredVolumeProfileShape[];
}

export class AnchoredVolumeProfile extends React.Component<AnchoredVolumeProfileProps, AnchoredVolumeProfileState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: Record<string, any> | null = null;

  static get defaultProps() {
    if (!AnchoredVolumeProfile._cachedDefaultProps) {
      AnchoredVolumeProfile._cachedDefaultProps = {
        onStart: noop,
        onSelect: noop,
        currentPositionStroke: '#1e53e6',
        currentPositionstrokeOpacity: 1,
        currentPositionStrokeWidth: 1,
        currentPositionRadius: 4,
        shouldDisableSnap: (e: React.MouseEvent) => e.button === 2 || e.shiftKey,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 'auto',
          bgWidth: 'auto',
          text: 'Click to select anchored volume profile',
          selectedText: '',
        },
        trends: [],
      };
    }
    return AnchoredVolumeProfile._cachedDefaultProps;
  }

  private getSelectionState: (moreProps: MoreProps) => boolean;
  private mouseMoved: boolean = false;
  private saveNodeType: <T>(type: string | number) => React.Ref<T>;
  private terminate: () => void;

  public constructor(props: AnchoredVolumeProfileProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.getSelectionState = isHoverForInteractiveType('trends').bind(this);

    this.state = {};
  }

  public render() {
    const {
      currentPositionstrokeOpacity = AnchoredVolumeProfile.defaultProps.currentPositionstrokeOpacity,
      currentPositionRadius = AnchoredVolumeProfile.defaultProps.currentPositionRadius,
      currentPositionStroke = AnchoredVolumeProfile.defaultProps.currentPositionStroke,
      currentPositionStrokeWidth = AnchoredVolumeProfile.defaultProps.currentPositionStrokeWidth,
      enabled,
      hoverText,
      shouldDisableSnap = AnchoredVolumeProfile.defaultProps.shouldDisableSnap,
      snap,
      snapTo,
      trends,
      onDoubleClick,
      sharedState,
      isAnyShapeEnabled,
    } = this.props;
    const { override } = this.state;

    return (
      <g>
        {trends.map((each, idx) => {
          if (each.hidden) return null;
          // 좌표가 아직 복원되지 않은 경우 (candleData 로드 전) 렌더링 건너뛰기
          if (!each.start) return null;

          const eachAppearance = {
            ...toVolumeProfileRFCProps(each as unknown as AnchoredVolumeProfileRenderNode),
            volumeProfile: true,
          };

          const hoverTextWithDefault = {
            ...AnchoredVolumeProfile.defaultProps.hoverText,
            ...hoverText,
          };

          return (
            <EachAnchoredVolumeProfile
              key={each.uuid}
              uuid={each.uuid}
              ref={this.saveNodeType(idx)}
              index={idx}
              selected={(each as unknown as { selected?: boolean }).selected}
              anchorX={getValueFromOverride(override, each.uuid, 'anchorX', each.start[0])}
              anchorY={each.start[1]}
              appearance={eachAppearance}
              hoverText={hoverTextWithDefault}
              onDrag={this.handleDragLine}
              onDragComplete={this.handleDragLineComplete}
              edgeInteractiveCursor="big-declined-arrow-cursor"
              lineInteractiveCursor="react-financial-charts-move-cursor"
              onSelect={this.handleSelect}
              onHover={this.handleHover}
              locked={each.locked}
              onDoubleClick={onDoubleClick}
              sharedState={sharedState ?? {}}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        <MouseLocationIndicator
          enabled={enabled ?? false}
          snap={snap ?? false}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
        />
      </g>
    );
  }

  private readonly handleHover = (hoveredId: string | null, moreProps: MoreProps) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(hoveredId, moreProps);
    }
  };

  private readonly handleSelect = (e: React.MouseEvent, selectedUuid: string, moreProps: MoreProps) => {
    const { trends } = this.props;
    const newTrends = trends.map(trend => ({
      ...trend,
      selected: trend.uuid === selectedUuid,
    }));

    const { onSelect } = this.props;
    if (onSelect !== undefined) {
      onSelect(e, newTrends, moreProps);
    }
  };

  /**
   * AVP creates a shape immediately with a single click.
   * (Unlike FRVP, there is no handleDrawLine / handleEnd flow.)
   */
  private readonly handleStart = (e: React.MouseEvent, _xyValue: number[], moreProps: MoreProps) => {
    const { enabled, trends, chartId } = this.props;
    if (!enabled) return;

    const { fullData, xAccessor } = moreProps;
    if (!fullData || fullData.length === 0) {
      return;
    }

    // Use a candle-center snapped coordinate (same behavior as FRVP)
    const [snappedX, snappedY] = getNewXY(moreProps);
    const clickX = snappedX;

    // X value of the first candle
    const firstCandleX = xAccessor(fullData[0]);
    // X value of the last candle
    const lastCandleX = xAccessor(fullData[fullData.length - 1]);

    // Validation: the anchor must be within the data range
    if (clickX < firstCandleX) {
      return;
    }

    // Clamp to the last candle (cannot start beyond the available data)
    const anchorX = Math.min(clickX, lastCandleX);

    // Ensure there are at least 2 candles (from anchorX to lastCandleX)
    const startIndex = fullData.findIndex(d => xAccessor(d) >= anchorX);
    if (startIndex === -1 || startIndex >= fullData.length - 1) {
      return;
    }

    const lastProps = getActiveTemplateNode(ShapeType.ANCHORED_VOLUME_PROFILE);
    const mergedAppearance = {
      ...volumeProfileDefaults,
      ...lastProps,
      volumeProfile: true,
    };

    // Y will be computed later as the POC position (use snapped Y as a temporary placeholder)
    const shapeUuid = crypto.randomUUID();
    const now = Date.now();
    const newShape: AnchoredVolumeProfileShape = {
      uuid: shapeUuid,
      type: ShapeType.ANCHORED_VOLUME_PROFILE,
      start: [anchorX, snappedY],
      selected: true,
      locked: false,
      chartId: chartId,

      appearance: mergedAppearance,
      hidden: false,
      createdAt: now,
      updatedAt: now,
    };

    const newTrends = [...trends.map(d => ({ ...d, selected: false })), newShape];

    this.setState({ trends: newTrends }, () => {
      const { onComplete, setSharedState } = this.props;

      // Reset shared state
      setSharedState?.({
        status: 'idle',
        type: undefined,
        chartId: undefined,
        startXY: undefined,
        endXY: undefined,
      });

      onComplete?.(e, newTrends, moreProps);
    });
  };

  private readonly handleDragLineComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { override } = this.state;
    if (!isDefined(override)) return;

    const { fullData, xAccessor } = moreProps;
    if (!fullData || fullData.length === 0) return;

    const lastCandleX = xAccessor(fullData[fullData.length - 1]);
    const firstCandleX = xAccessor(fullData[0]);

    // Validation
    let newAnchorX = override.anchorX;
    if (newAnchorX < firstCandleX) {
      newAnchorX = firstCandleX;
    }
    if (newAnchorX > lastCandleX) {
      newAnchorX = lastCandleX;
    }

    // Ensure at least 2 candles remain
    const startIndex = fullData.findIndex(d => xAccessor(d) >= newAnchorX);
    if (startIndex === -1 || startIndex >= fullData.length - 1) {
      this.setState({ override: null });
      return;
    }

    const { trends, onDragComplete } = this.props;
    const targetUuid = override.uuid;
    const newTrends: AnchoredVolumeProfileShape[] = trends.map(each =>
      each.uuid === targetUuid
        ? {
            ...each,
            start: [newAnchorX, each.start[1]] as [number, number],
            selected: true,
          }
        : { ...each, selected: false },
    );

    this.setState({ override: null }, () => {
      onDragComplete?.(e, newTrends, moreProps);
    });
  };

  private readonly handleDragLine = (
    _: React.MouseEvent,
    uuid: string | undefined,
    newXYValue: { anchorX: number; lastCandleX?: number; firstCandleX?: number },
  ) => {
    const { hoverId } = this.props;
    const { override } = this.state;
    if (uuid === undefined) return;
    if (override && override.uuid !== uuid) return;

    if (hoverId && hoverId !== uuid) return;
    this.mouseMoved = true;

    const { anchorX, lastCandleX, firstCandleX } = newXYValue;
    let clampedAnchorX = anchorX;

    // Clamp within the data range
    if (firstCandleX !== undefined && clampedAnchorX < firstCandleX) {
      clampedAnchorX = firstCandleX;
    }
    if (lastCandleX !== undefined && clampedAnchorX > lastCandleX) {
      clampedAnchorX = lastCandleX;
    }

    this.setState({
      override: {
        uuid,
        anchorX: clampedAnchorX,
      },
    });
  };
}
